import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    AlertTriangle, CheckCircle2, FileUp, Gauge, Loader2, PlayCircle, ShieldCheck, Sparkles, Trash2, Wand2,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Panel } from "@/components/common/Panel";
import { SeverityBadge } from "@/components/common/Badges";
import { TID } from "@/lib/testIds";

const SEV_ORDER = ["critical", "high", "medium", "low"];

function scoreTone(score) {
    if (score >= 85) return "text-emerald-300";
    if (score >= 60) return "text-amber-300";
    return "text-rose-300";
}

/** Dynamic PFCG Role Content Update — library-wide conformance sweep plus the
 * customer best-practice pack manager. */
export function BestPracticeSweepPanel({ roles, openTools, isAdmin }) {
    const [sweep, setSweep] = useState(null);
    const [loading, setLoading] = useState(false);
    const [pack, setPack] = useState(null);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    const loadPack = useCallback(async () => {
        try {
            const { data } = await api.get("/best-practices");
            setPack(data);
        } catch {
            setPack(null);
        }
    }, []);

    useEffect(() => { loadPack(); }, [loadPack]);

    const runSweep = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/best-practices/sweep");
            setSweep(data);
            toast.success(`${data.roles_scanned} role(s) scanned · ${data.open_total} open proposal(s)`);
        } catch {
            toast.error("Sweep failed");
        } finally {
            setLoading(false);
        }
    };

    const uploadPack = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const form = new FormData();
            form.append("file", file);
            const { data } = await api.post("/best-practices/upload", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(`${data.imported} rule(s) imported as ${data.status}`);
            await loadPack();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Pack import failed");
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const activate = async (id) => {
        try {
            await api.post(`/best-practices/rules/${id}/activate`);
            toast.success("Rule activated");
            await loadPack();
        } catch {
            toast.error("Could not activate the rule");
        }
    };

    const remove = async (id) => {
        try {
            await api.delete(`/best-practices/rules/${id}`);
            toast.success("Rule removed");
            await loadPack();
        } catch {
            toast.error("Could not remove the rule");
        }
    };

    return (
        <div className="space-y-6" data-testid={TID.bpSweepPanel}>
            <Panel
                testId={TID.bpSweepCard}
                eyebrow="Dynamic PFCG role content update"
                title="Best-Practice Conformance Sweep"
                action={
                    <button
                        data-testid={TID.bpRunSweep}
                        onClick={runSweep}
                        disabled={loading}
                        className="btn-primary inline-flex items-center gap-1.5 text-xs disabled:opacity-50"
                    >
                        {loading ? <Loader2 size={12} className="animate-spin" /> : <PlayCircle size={12} />}
                        Run sweep
                    </button>
                }
            >
                {!sweep ? (
                    <p className="text-sm text-slate-400">
                        Run the sweep to score all {roles?.length ?? 0} roles against {pack?.counts?.curated ?? 0} curated
                        rules{pack?.counts?.uploaded_active ? ` and ${pack.counts.uploaded_active} of your own` : ""}.
                    </p>
                ) : (
                    <div className="space-y-3">
                        <div className="flex flex-wrap items-center gap-3">
                            <div className="flex items-center gap-2" data-testid={TID.bpSweepAverage}>
                                <Gauge size={16} className={scoreTone(sweep.average_score)} />
                                <span className={`text-2xl font-mono ${scoreTone(sweep.average_score)}`}>{sweep.average_score}</span>
                                <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400">avg conformance</span>
                            </div>
                            {SEV_ORDER.filter((s) => sweep.totals_by_severity[s]).map((s) => (
                                <span key={s} className="flex items-center gap-1.5">
                                    <SeverityBadge severity={s} />
                                    <span className="font-mono text-xs text-slate-300">{sweep.totals_by_severity[s]}</span>
                                </span>
                            ))}
                            <span className="text-[10px] font-mono text-slate-400">pack {sweep.pack_version}</span>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full text-[11px]">
                                <thead>
                                    <tr className="text-left font-mono uppercase tracking-wider text-[9px] text-slate-400 border-b border-white/10">
                                        <th className="py-1.5 pr-3">Role</th>
                                        <th className="py-1.5 pr-3">Score</th>
                                        <th className="py-1.5 pr-3">Open</th>
                                        <th className="py-1.5 pr-3">Auto-applicable</th>
                                        <th className="py-1.5 pr-3">Needs values</th>
                                        <th className="py-1.5 pr-3">Advisory</th>
                                        <th className="py-1.5" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {sweep.rows.map((r) => (
                                        <tr key={r.role_id} data-testid={TID.bpSweepRow(r.role_id)} className="border-b border-white/5 hover:bg-white/[0.02]">
                                            <td className="py-1.5 pr-3 font-mono text-slate-200">{r.role_name}</td>
                                            <td className={`py-1.5 pr-3 font-mono ${scoreTone(r.score)}`}>{r.score}</td>
                                            <td className="py-1.5 pr-3 font-mono text-slate-300">{r.open_count}</td>
                                            <td className="py-1.5 pr-3 font-mono text-emerald-300">{r.auto_applicable}</td>
                                            <td className="py-1.5 pr-3 font-mono text-amber-300">{r.needs_input}</td>
                                            <td className="py-1.5 pr-3 font-mono text-slate-400">{r.advisory}</td>
                                            <td className="py-1.5">
                                                <button
                                                    data-testid={TID.bpSweepOpen(r.role_id)}
                                                    onClick={() => openTools?.((roles || []).find((x) => x.id === r.role_id), "best-practice")}
                                                    className="btn-ghost inline-flex items-center gap-1 text-[10px]"
                                                >
                                                    <Wand2 size={11} /> Review
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="space-y-1 border-t border-white/5 pt-2">
                            <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400">Most frequent findings</p>
                            {sweep.by_rule.slice(0, 8).map((e) => (
                                <div key={e.rule_id} className="flex flex-wrap items-center gap-2 text-[11px]">
                                    <SeverityBadge severity={e.severity} />
                                    <span className="font-mono text-cyan-300">{e.rule_id}</span>
                                    <span className="text-slate-200">{e.title}</span>
                                    <span className="font-mono text-slate-400">{e.roles} role(s) · {e.proposals} proposal(s)</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </Panel>

            <Panel
                testId={TID.bpPackCard}
                eyebrow="Best-practice pack"
                title="Feed your own SAP best practices"
                action={
                    isAdmin ? (
                        <>
                            <input
                                ref={fileRef}
                                type="file"
                                accept=".csv,.xlsx,.xlsm,.txt,.md,.pdf,.docx"
                                onChange={(e) => uploadPack(e.target.files?.[0])}
                                className="hidden"
                                data-testid={TID.bpPackInput}
                            />
                            <button
                                data-testid={TID.bpPackUpload}
                                onClick={() => fileRef.current?.click()}
                                disabled={uploading}
                                className="btn-ghost inline-flex items-center gap-1.5 text-xs disabled:opacity-50"
                            >
                                {uploading ? <Loader2 size={12} className="animate-spin" /> : <FileUp size={12} />}
                                Import pack
                            </button>
                        </>
                    ) : null
                }
            >
                {!pack ? (
                    <p className="text-sm text-slate-400">Pack unavailable.</p>
                ) : (
                    <div className="space-y-3">
                        <div className="flex flex-wrap items-center gap-2 text-[10px] font-mono">
                            <span className="px-1.5 py-0.5 rounded-sm bg-cyan-500/15 text-cyan-300 border border-cyan-500/30">
                                {pack.counts.curated} curated
                            </span>
                            <span className="px-1.5 py-0.5 rounded-sm bg-violet-500/10 text-violet-300 border border-violet-500/25">
                                {pack.counts.uploaded_active} yours (active)
                            </span>
                            <span className="px-1.5 py-0.5 rounded-sm bg-amber-500/10 text-amber-300 border border-amber-500/25">
                                {pack.counts.candidates} candidate(s)
                            </span>
                            <span className="text-slate-400">{pack.pack_version}</span>
                        </div>

                        <p className="text-[10px] font-mono text-slate-400">
                            Structured columns: {pack.structured_columns.join(" · ")}
                        </p>

                        {pack.uploaded.length > 0 && (
                            <div className="space-y-1" data-testid={TID.bpPackActive}>
                                <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400">Active customer rules</p>
                                {pack.uploaded.map((r) => (
                                    <div key={r.id} className="flex flex-wrap items-center gap-2 text-[11px]">
                                        <CheckCircle2 size={12} className="text-emerald-400" />
                                        <span className="font-mono text-cyan-300">{r.rule_id}</span>
                                        <span className="text-slate-200">{r.title}</span>
                                        <span className="font-mono text-slate-400">
                                            {r.object || r.forbidden_tcodes}{r.field ? `-${r.field}` : ""}
                                        </span>
                                        {isAdmin && (
                                            <button
                                                data-testid={TID.bpRuleDelete(r.id)}
                                                onClick={() => remove(r.id)}
                                                className="text-slate-500 hover:text-rose-400 transition-colors"
                                                aria-label={`Remove ${r.rule_id}`}
                                            >
                                                <Trash2 size={11} />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {pack.candidates.length > 0 && (
                            <div className="space-y-1 border-t border-white/5 pt-2" data-testid={TID.bpPackCandidates}>
                                <p className="text-[10px] font-mono uppercase tracking-wider text-amber-300 flex items-center gap-1.5">
                                    <AlertTriangle size={11} /> Extracted candidates — inert until activated
                                </p>
                                {pack.candidates.map((r) => (
                                    <div key={r.id} className="flex flex-wrap items-center gap-2 text-[11px]">
                                        <span className="font-mono text-slate-400">{r.extracted_from}</span>
                                        <span className="text-slate-200">{r.rationale?.slice(0, 110)}</span>
                                        {isAdmin && (
                                            <>
                                                <button
                                                    data-testid={TID.bpRuleActivate(r.id)}
                                                    onClick={() => activate(r.id)}
                                                    className="btn-ghost inline-flex items-center gap-1 text-[10px]"
                                                >
                                                    <Sparkles size={11} /> Activate
                                                </button>
                                                <button
                                                    data-testid={TID.bpRuleDelete(r.id)}
                                                    onClick={() => remove(r.id)}
                                                    className="text-slate-500 hover:text-rose-400 transition-colors"
                                                    aria-label={`Remove candidate`}
                                                >
                                                    <Trash2 size={11} />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {pack.uploaded.length === 0 && pack.candidates.length === 0 && (
                            <p className="text-sm text-slate-400 flex items-center gap-1.5">
                                <ShieldCheck size={13} className="text-cyan-400" />
                                Only the curated pack is active. Import your own standard to have it enforced alongside it.
                            </p>
                        )}
                    </div>
                )}
            </Panel>
        </div>
    );
}
